"use client"

import { getPageNameFromPath } from "@/app/(site)/_utils/getPageNameFromPath"
import useEcommerceStore from "@/app/(site)/contexts/ecommerce/store"
import { viewItemListEvent } from "@/lib/utils/tracking/gtmEvents"
import { adaptProductForTracking } from "@/lib/utils/tracking/itemDataTransformer"
import { useSiteContext } from "@/lib/utils/tracking/siteContext"
import { CheckoutAPIProduct } from "@/types/centra/checkout-api/products/post-products"
import { usePathname } from "next/navigation"
import { useEffect, useState } from "react"

interface CarouselTrackingScriptProps {
    carouselId: string
    carouselTitle: string
    productData: CheckoutAPIProduct[]
    locale: string
}

export default function CarouselTrackingScript({
    carouselId,
    carouselTitle,
    productData
}: CarouselTrackingScriptProps) {
    const [hasTracked, setHasTracked] = useState(false)
    const [isOutOfView, setIsOutOfView] = useState(false)
    const centraSession = useEcommerceStore(state => state.centraSession)
    const siteContext = useSiteContext()
    const path = usePathname()
    const pageName = getPageNameFromPath(path)
    useEffect(() => {
        const carouselElement = document.getElementById(carouselId)

        if (!carouselElement || productData.length === 0) return

        const observer = new IntersectionObserver(
            entries => {
                const entry = entries[0]

                if (entry.isIntersecting) {
                    // Carousel is in view
                    setIsOutOfView(false)

                    // Track if we haven't tracked yet, or if we've been out of view and came back
                    if (!hasTracked || isOutOfView) {
                        setHasTracked(true)

                        try {
                            const trackingItems = productData.map(product =>
                                adaptProductForTracking(product, {
                                    item_list_name: "carousel",
                                    item_list_id: `${pageName}|${carouselTitle}`,
                                    quantity: 1
                                })
                            )

                            viewItemListEvent(
                                trackingItems,
                                "carousel",
                                `${pageName}|${carouselTitle}`,
                                siteContext,
                                "carousel",
                                `${pageName}|${carouselTitle}`
                            )
                        } catch (error) {
                            console.error("Error in carousel tracking:", error)
                        }
                    }
                } else {
                    // Carousel is out of view
                    setIsOutOfView(true)
                }
            },
            { threshold: 0.1 }
        )

        observer.observe(carouselElement)

        return () => {
            observer.disconnect()
        }
    }, [
        carouselId,
        carouselTitle,
        productData,
        pageName,
        centraSession,
        siteContext,
        hasTracked,
        isOutOfView
    ])

    return null
}
